"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Box,
  Button,
  Checkbox,
  FormLabel,
  Grid,
  IconButton,
  TextField,
  TextFieldProps,
} from "@mui/material"
import { postWebBmbyLead } from "api"
import { useLocale, useTranslations } from "next-intl"
import { LegacyRef, useEffect, useId, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import {
  CheckboxIcon,
  CheckedCheckboxIcon,
  CircleXIcon,
  NewText,
  SuccessFailState,
  XIcon,
} from "ui"
import { sharedColors as colors, sharedColors } from "ui/theme/colors"
import { submitClick, submitError, submitSuccess } from "utils"
import { generateGenericSchema } from "utils/Form/FormSchema"
import * as yup from "yup"
import { ctaMessage } from "../Contact/ContactForm"
import CustomHelperText from "../Contact/CustomHelperText"
import { ProjectsMessages } from "./Projects"

const CHARACTER_LIMIT = 500

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

export type FormProjectProps = {
  formSubjects: Record<string, string>
  onClose: () => void
  messages: ProjectsMessages
  formData?: {
    price: number | undefined
    currency: string | undefined
    area: number | undefined
    floor: string | undefined
    rooms: string | undefined
  }
  bmbyId?: number
  status?: any
  title?: string
  type?: string
  companyId?: number
  emailForLead?: string
  newStyle?: boolean
  assetName?: string
}

const StyledCheckboxContainer = styled.div<{ newStyle?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
  }
  ${({ newStyle, theme }) =>
    newStyle &&
    `
  span div {
    background-color: ${theme.colors.blue4rentMarketing} !important;
  }
  `}
`

const CheckBoxesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
`

const FormContainer = styled.div`
  --text-black: #011727;
  min-width: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => props.theme.breakpoints.down("md")} {
    min-width: 0;
    width: 100%;
    height: 100%;
  }
  &.newStyle {
    height: 100%;
    position: relative;
    flex-direction: column;
    min-width: unset;
    overflow: hidden;
  }
`
const StyledForm = styled.form`
  ${(props) => props.theme.breakpoints.down("md")} {
    height: 100%;
  }
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  border: 2px solid ${(props) => props.theme.colors.propBlue};
  background: white;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 660px;
  }
  .MuiFormHelperText-root {
    bottom: -25px;
    line-height: 1;
  }
  &.newStyle {
    border-radius: 0;
    border: none;
    height: 100%;
    overflow: auto;
    flex: 1;
    width: 100%;
    .MuiFilledInput-root.MuiInputBase-multiline {
      padding-top: 8px !important;
    }
    .MuiInputBase-input:not(.MuiInputBase-inputMultiline) {
      padding-top: 8px !important;
    }
    .MuiInputBase-inputMultiline {
    }
  }
`

const StyledInsideForm = styled.div`
  padding: 24px;
  /* max-height: 500px; */
  overflow-y: scroll;

  ${(props) => props.theme.breakpoints.up("md")} {
    max-height: none;
    overflow-y: auto;
    max-height: 600px;
  }

  &.newStyle {
    flex: 1;
    max-height: unset;
  }
`
const StyledHeadingH2 = styled.h2`
  font-family: var(--narkis-condensed) !important;
  font-size: 32px;
  font-weight: 400;
  line-height: 150%;
  margin: 0;
  display: flex;
  width: max-content;
  flex-direction: column;
  width: 1px;
  max-width: max-content;
  flex: 1;
  &::after {
    content: "";
    background-image: url("/icons/heading-underline-blue-mobile.svg");
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    display: flex;
    width: 100%;
    height: 4px;
    position: relative;
    top: -4px;
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px; /* 90px */
    letter-spacing: 0.6px;
    &::after {
      background-image: url("/icons/heading-underline-blue-desktop.svg");
    }
  }
`

const StyledHeader = styled.div`
  height: 84px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.theme.colors.propBlue};
  border-radius: 10px 10px 0 0;
  display: flex;
  padding: 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 24px;
  }
  &.newStyle {
    height: unset;
    border-radius: 0;
    background-color: white;
    color: var(--text-black);
    align-items: flex-start;
    z-index: 1;
    position: relative;
    flex-shrink: 0;
    width: 100%;
  }
`
const StyledButton = styled(Button)`
  width: 153px;
`

const BottomContainer = styled.div<{ withFormData?: boolean }>`
  border-top: 1px solid black;
  padding: 24px;
  display: flex;
  gap: 16px;
  flex-direction: column;
  ${(props) => props.theme.breakpoints.down("sm")} {
    .grecaptcha-badge {
      height: 45px !important;
    }
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: stretch;
    flex-direction: row;
    gap: 24px;
    margin-bottom: 0px;
  }

  &.newStyle {
    border-top: none;
    background-color: ${({ theme }) => theme.colors.darkBlue4rentMarketing};
    color: ${({ withFormData }) => (withFormData ? "white" : "")};
    ${(props) => props.theme.breakpoints.up("md")} {
      background-color: ${({ withFormData, theme }) =>
        withFormData ? theme.colors.darkBlue4rentMarketing : "transparent"};
    }
  }
`
const TopDetailsDiv = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: max-content;
    flex-direction: column;
    align-items: start;
  }
`

const DetailNumbers = styled.div`
  display: flex;
  gap: 16px;
`
const BottomDetailsDiv = styled.div`
  width: 100%;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: end;
  }
`
const PriceDiv = styled.div`
  display: flex;
  flex-direction: column;
`
const StyledTitle = styled.div`
  margin-bottom: 16px;
`

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: boolean }>`
  padding: 1px 10px 0px 0;
  ${(props) =>
    !props.status
      ? `  rect {
    stroke: red;
  }`
      : ""}
`

const StyledButtonNew = styled(Button)`
  border-radius: 8px;
  font-weight: 700;
  font-size: 20px;
  line-height: 24px;
  letter-spacing: 0px;
  padding: 12px 5px;
  justify-content: center;
  min-width: 200px;
`

const BlueStyledButton = styled(StyledButtonNew)<{ fixedWidth?: boolean }>`
  --theme-color: ${(props) => props.theme.colors.blue4rentMarketing};
  color: white;
  background-color: var(--theme-color);
  border-color: var(--theme-color);
  flex: ${({ fixedWidth }) => (fixedWidth ? "unset" : "1")};
  &[disabled] {
    color: white;
    background-color: ${(props) => props.theme.colors.gray4rentMarketing};
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    width: unset;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.blue4rentMarketingHover};
  }
`

// const ContactUsSubjectsArray: ContactUsSubjects[] = [
//   "AshtromGroupGeneral",
//   "AshtromGroupInvestorRelations",
//   "AshdarCustomerFinance_Simha",
//   "AshdarCustomerFinance_Ravit",
//   "AshdarCustomerFinance_Osnat",
//   "AshdarCustomerFinance_Odet",
//   "AshdarMarketingAndSales",
//   "AshdarUrbanRenewalDepartment",
//   "AshdarRightApprovalsAndMortgages",
//   "AshdarPlanningDepartment",
//   "AshdarCommonHouseRegistration",
//   "AshdarGeneral",
//   "AshdarEngineeringDepartment",
//   "PropertiesGeneral",
//   "PropertiesFinanceAndAccounting",
//   "PropertiesEngineeringDivision",
//   "PropertiesProjectMarketing",
//   "PropertiesAshtromPORT_ManagedOffices",
//   "ContractingGeneral",
//   "ResidentialRentingGeneral",
//   "FranchisingGeneral",
//   "IndustriesGeneral",
//   "RenewableEnergyGeneral",
//   "InterationalBusinessDevelopment",
//   "InterationalMarketing",
//   "InterationalProjects",
//   "InterationalGeneral",
//   "AshtromEngineeringAndConstructionGeneral",
//   "AshtromNorthGeneral",
//   "AshtromJerusalemGeneral",
//   "RoadsGeneral",
//   "AshkritGeneral",
//   "Basepyle",
//   "MLTGeneral",
// ]

const SmallDivider = styled.div`
  height: 14px;
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 24px;
  }
  width: 1px;
  background-color: #6f6f6f;
`

const StyledClose = styled(Button)`
  margin: 0;
  padding: 0;
  height: 24px;
  width: 24px;
  min-width: 0;
`
type CustomInputProps = {
  separateLabel?: boolean
  label: string
}

const CustomInput = ({ separateLabel, label, ...props }: TextFieldProps & CustomInputProps) => {
  const uniqueId = useId()
  return separateLabel ? (
    <>
      <FormLabel htmlFor={uniqueId} style={{ color: "#011727", fontSize: 16, fontWeight: 300 }}>
        {label}
      </FormLabel>
      <TextField
        {...props}
        id={uniqueId}
        variant={"filled"}
        error={!!props.error}
        fullWidth
        inputProps={props.inputProps}
      />
    </>
  ) : (
    <TextField
      {...props}
      variant={props.variant || "outlined"}
      label={label}
      error={!!props.error}
      fullWidth
      inputProps={props.inputProps}
    />
  )
}

function ContactForm({
  onClose,
  messages,
  formData,
  bmbyId,
  status,
  title,
  companyId,
  emailForLead,
  newStyle,
  type,
}: FormProjectProps) {
  const [formIsSubmitted, setFormIsSubmitted] = useState(false)
  const [submitState, setSubmitState] = useState<"success" | "fail">("fail")
  const reCaptchaRef = useRef<ReCAPTCHA | null>()
  const locale = useLocale()
  const schema = generateGenericSchema(locale === "he" ? "he" : "en")
  const t = useTranslations("ProjectsPage")
  const tAccordion = useTranslations("AccordionDetails")
  const [checkedMarketing, setCheckedMarketing] = useState<boolean>(false)
  const [checkedCommercial, setCheckedCommercial] = useState<boolean>(false)
  const getPorjectStatues = () => {
    if (status === "building") return "Building"
    if (status === "Marketing and sales") return "MarketingAndSales"
    if (status === "Marketing and  Rental") return "MarketingAndRental"
    if (status === "planning") return "Planning"
    if (status === "Completed") return "Completed"
    if (status === "Occupancy") return "Occupancy"
    if (status === "management and operation") return "ManagementAndOperation"
  }

  type FormInput = yup.InferType<typeof schema>
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isSubmitted, isValid, isSubmitting },
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
  })
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    reCaptchaRef?.current?.reset()
    const recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string
    try {
      const senddata = {
        projectId: bmbyId || -1,
        // projectId: 2904,
        name: data.fullName,
        email: data.email,
        phone: data.phone,
        comments: data.message || "",
        gRecaptchaResponse: recaptchaResponse,
        projectStatus: getPorjectStatues() as string,
        allowedMarketing: checkedMarketing,
        allowedSmsAndEmail: checkedCommercial,
        companyId: companyId || -1,
        uri: window?.location?.href,
        toEmail: emailForLead || "",
      }
      const response = await postWebBmbyLead(senddata)
      if (response?.data?.body?.addLeadSucceeded) {
        submitSuccess("שליחה", "השאירו פרטים ונחזור אליכם", true)
        setSubmitState("success")
        setFormIsSubmitted(true)
        reset()
      } else {
        setSubmitState("fail")
        setFormIsSubmitted(true)
        reset()
        submitError("שליחה", messages.forYou, response?.data?.body?.errorMessage)
      }
    } catch (error) {
      setSubmitState("fail")
      setFormIsSubmitted(true)
      reset()
      submitError("שליחה", messages.forYou, error as any)
    }
  }

  useEffect(() => {
    if (!formIsSubmitted) {
      reset()
    }
  }, [formIsSubmitted, reset])

  return (
    <FormContainer className={`${newStyle ? "newStyle" : ""}`}>
      <StyledForm onSubmit={handleSubmit(onSubmit)} className={`${newStyle ? "newStyle" : ""}`}>
        <StyledHeader className={`${newStyle ? "newStyle" : ""}`}>
          {newStyle ? (
            <>
              <StyledHeadingH2>{title ? title : messages.forYou}</StyledHeadingH2>
              <IconButton
                sx={{
                  flexShrink: 0,
                  marginInlineStart: "10px",
                  borderRadius: "50%",
                  border: "1px solid #F7F7F7",
                  background: "rgba(255, 255, 255, 0.50)",
                }}
                onClick={onClose}
              >
                <XIcon width={20} height={20} />
              </IconButton>
            </>
          ) : (
            <>
              <NewText
                component="p"
                desktopFontSize={32}
                desktopLineHeight="36px"
                mobileFontSize={24}
                mobileLineHeight="28px"
                color={colors.white}
              >
                {title ? title : messages.forYou}
              </NewText>
              <StyledClose onClick={onClose}>
                <CircleXIcon />
              </StyledClose>
            </>
          )}
        </StyledHeader>

        {formIsSubmitted ? (
          <div
            style={{
              ...(newStyle
                ? {
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                    padding: 16,
                  }
                : { padding: 24 }),
            }}
          >
            <SuccessFailState
              setIsSubmitted={setFormIsSubmitted}
              state={submitState}
              messages={messages}
              type={(type as any) || "project"}
              onClose={onClose}
              newStyle={newStyle}
            />
          </div>
        ) : (
          <>
            <StyledInsideForm className={`${newStyle ? "newStyle" : ""}`}>
              <StyledTitle>
                <NewText
                  component="p"
                  desktopFontSize={newStyle ? 14 : 16}
                  desktopLineHeight={newStyle ? "14px" : "20px"}
                  mobileFontSize={14}
                  mobileLineHeight="14px"
                  color={colors.black}
                >
                  {messages.required}
                </NewText>
              </StyledTitle>
              <Grid container columnSpacing={2} rowSpacing={4}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="fullName"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        separateLabel={newStyle}
                        label={
                          newStyle ? messages?.newFullName || messages.fullName : messages.fullName
                        }
                        variant="filled"
                        error={!!errors.fullName}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.fullName}
                            text={errors.fullName?.message}
                          />
                        }
                        fullWidth
                        inputProps={{
                          style: {
                            fontSize: 16,
                            ...(newStyle ? { lineHeight: "24px", height: 32 } : {}),
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        separateLabel={newStyle}
                        label={messages.telephone}
                        variant="filled"
                        error={!!errors.phone}
                        inputProps={{
                          style: {
                            fontSize: 16,
                            ...(newStyle ? { lineHeight: "24px", height: 32 } : {}),
                          },
                        }}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.phone}
                            text={errors.phone?.message}
                          />
                        }
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        separateLabel={newStyle}
                        label={newStyle ? messages.newEmail || messages.email : messages.email}
                        variant="filled"
                        error={!!errors.email}
                        inputProps={{
                          style: {
                            fontSize: 16,
                            ...(newStyle ? { lineHeight: "24px", height: 32 } : {}),
                          },
                        }}
                        helperText={
                          <CustomHelperText
                            render={isSubmitted || isSubmitting}
                            error={!!errors.email}
                            text={errors.email?.message}
                          />
                        }
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="message"
                    control={control}
                    render={({ field }) => (
                      <CustomInput
                        {...field}
                        separateLabel={newStyle}
                        label={messages.messageOptional}
                        variant="filled"
                        multiline
                        minRows={3}
                        FormHelperTextProps={{ sx: { color: "black" } }}
                        inputProps={{
                          maxLength: CHARACTER_LIMIT,
                          style: { fontSize: 16, resize: "vertical" },
                        }}
                        error={!!errors.message}
                        helperText={`${field?.value?.length || 0}/${CHARACTER_LIMIT || 0}`}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CheckBoxesContainer>
                    <StyledCheckboxContainer newStyle={newStyle}>
                      <StyledCheckbox
                        icon={<CheckboxIcon />}
                        checkedIcon={<CheckedCheckboxIcon />}
                        sx={{
                          ":hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                        status={true}
                        value={checkedMarketing}
                        onChange={() => setCheckedMarketing((el) => !el)}
                      />

                      <div>
                        <NewText
                          desktopFontSize={14}
                          desktopFontWeight="400"
                          desktopLineHeight="20px"
                          mobileFontSize={12}
                          mobileFontWeight="400"
                          mobileLineHeight={"17px"}
                          component="p"
                          color={"black"}
                          maxWidth={"750px"}
                          dangerouslySetInnerHTML={{
                            __html: t("marketingMessage"),
                          }}
                        ></NewText>
                      </div>
                    </StyledCheckboxContainer>
                    <StyledCheckboxContainer newStyle={newStyle}>
                      <StyledCheckbox
                        icon={<CheckboxIcon />}
                        checkedIcon={<CheckedCheckboxIcon />}
                        sx={{
                          ":hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                        status={true}
                        value={checkedCommercial}
                        onChange={() => setCheckedCommercial((el) => !el)}
                      />

                      <div>
                        <NewText
                          desktopFontSize={14}
                          desktopFontWeight="400"
                          desktopLineHeight="20px"
                          mobileFontSize={12}
                          mobileFontWeight="400"
                          mobileLineHeight={"17px"}
                          component="p"
                          color={"black"}
                          maxWidth={"750px"}
                          dangerouslySetInnerHTML={{
                            __html: t("commercialMessage"),
                          }}
                        ></NewText>
                      </div>
                    </StyledCheckboxContainer>
                  </CheckBoxesContainer>
                  <div style={{ paddingTop: "16px" }}>
                    <NewText
                      desktopFontSize={14}
                      desktopFontWeight="400"
                      desktopLineHeight="20px"
                      mobileFontSize={12}
                      mobileFontWeight="400"
                      mobileLineHeight={"17px"}
                      component="p"
                      color={"black"}
                      maxWidth={"750px"}
                      dangerouslySetInnerHTML={{
                        __html: ctaMessage(t("ctaMessage") || ""),
                      }}
                    ></NewText>
                  </div>
                </Grid>
              </Grid>
            </StyledInsideForm>
            <BottomContainer className={`${newStyle ? "newStyle" : ""}`} withFormData>
              {formData ? (
                <TopDetailsDiv>
                  <NewText
                    style={{ marginInlineEnd: "auto" }}
                    component={"span"}
                    desktopFontWeight={600}
                    desktopFontSize={16}
                    desktopLineHeight={"23px"}
                    mobileFontSize={14}
                    mobileLineHeight={"20px"}
                    color={newStyle ? "inherit !important" : sharedColors.black}
                    columnMobile={true}
                  >
                    דירה
                  </NewText>
                  <DetailNumbers>
                    <NewText
                      component={"span"}
                      desktopFontSize={16}
                      desktopLineHeight={"25.92px"}
                      mobileFontSize={18}
                      mobileLineHeight={"14px"}
                      style={{ textWrap: "nowrap" }}
                      color={newStyle ? "inherit" : sharedColors.black}
                    >
                      {formData.rooms} <span>{tAccordion("numberOfRooms")}</span>
                    </NewText>
                    <SmallDivider />
                    <NewText
                      component={"span"}
                      style={{ textWrap: "nowrap" }}
                      desktopFontSize={16}
                      desktopLineHeight={"25.92px"}
                      mobileFontSize={18}
                      mobileLineHeight={"14px"}
                      color={newStyle ? "inherit" : sharedColors.black}
                    >
                      <span>{tAccordion("floor")}</span> {formData.floor}
                    </NewText>
                    <SmallDivider />
                    <NewText
                      component={"span"}
                      desktopFontSize={16}
                      desktopLineHeight={"25.92px"}
                      mobileFontSize={18}
                      mobileLineHeight={"14px"}
                      style={{ textWrap: "nowrap" }}
                      color={newStyle ? "inherit" : sharedColors.black}
                    >
                      {formData.area} <span>מ״ר</span>
                    </NewText>
                    <SmallDivider />
                  </DetailNumbers>
                </TopDetailsDiv>
              ) : null}

              <BottomDetailsDiv>
                {formData ? (
                  <PriceDiv>
                    <NewText
                      component={"span"}
                      desktopFontSize={16}
                      style={{ textWrap: "nowrap" }}
                      desktopLineHeight={"20.16px"}
                      mobileFontSize={16}
                      mobileLineHeight={"20.16px"}
                      color={newStyle ? "inherit" : sharedColors.black}
                    >
                      מחיר דירה
                    </NewText>
                    {formData.price && (
                      <NewText
                        component={"span"}
                        desktopFontSize={32}
                        style={{ textWrap: "nowrap" }}
                        desktopLineHeight={"36px"}
                        mobileFontSize={24}
                        mobileLineHeight={"34px"}
                        desktopFontWeight={400}
                        color={newStyle ? "inherit" : sharedColors.black}
                      >
                        {new Intl.NumberFormat(locale).format(formData.price)}{" "}
                        <span style={{ fontSize: "18px" }}>{formData.currency}</span>
                      </NewText>
                    )}
                  </PriceDiv>
                ) : null}

                <Box
                  component={newStyle ? BlueStyledButton : StyledButton}
                  type="submit"
                  variant="outlined"
                  size="large"
                  fixedWidth={newStyle && !!formData}
                  style={{ marginInlineStart: "auto" }}
                  disabled={isSubmitting}
                  onClick={() => {
                    submitClick("שליחה", messages.forYou),
                      (Object.keys(errors).length > 0 || !isValid) &&
                        submitError(
                          "שליחה",
                          messages.forYou,
                          errors?.fullName?.message ||
                            errors?.email?.message ||
                            errors.phone?.message ||
                            "Fields are mandatory",
                        )
                  }}
                >
                  {isSubmitting ? t("sending") : t("send")}
                </Box>

                {RECAPTCHA_KEY && (
                  <ReCAPTCHA
                    ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
                    sitekey={RECAPTCHA_KEY}
                    size="invisible"
                    hl="iw"
                    tabIndex={-1}
                  />
                )}
              </BottomDetailsDiv>
            </BottomContainer>
          </>
        )}
      </StyledForm>
    </FormContainer>
  )
}

export default ContactForm
